import React, { useEffect, useState } from "react";
import { Calendar, ChevronDown } from "lucide-react";
import { Button } from "../../../components/ui/button";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "../../../components/ui/popover";
import { presetToPeriod, periodTriggerLabel } from "./uebersichtHelpers";
import { currentBusinessPeriod } from "../../../utils/datetime";

// 6A.3b: header period picker. Controlled — emits a {mode, from, to} period.
// Presets (left) cover the common ranges; a free Von–Bis (right) builds a custom
// bounded range. month-presets → mode "month"; ranges/free → "custom"; Gesamte
// Zeit → "all". The downstream wiring (6A.3a) maps these to the kpis fetch.

const PRESETS = [
  { key: "current", label: "Aktueller Monat" },
  { key: "prev", label: "Vorheriger Monat" },
  { key: "last3", label: "Letzte 3 Monate" },
  { key: "last6", label: "Letzte 6 Monate" },
  { key: "last12", label: "Letzte 12 Monate" },
  { key: "ytd", label: "Dieses Jahr" },
  { key: "prevYear", label: "Letztes Jahr" },
  { key: "all", label: "Gesamte Zeit" },
];

const MONTHS_DE = [
  "Januar", "Februar", "März", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Dezember",
];

// A preset is "active" when the value CAME FROM it (P4).
//
// This used to compare computed bounds, which meant a hand-picked Januar–September
// lit up "Dieses Jahr" — the picker asserting an intent the user never expressed,
// and the same confusion the Performance comparison had to be rescued from. The
// value now carries the key it was built with, so the highlight states a fact
// rather than a guess, and a custom range that happens to match a preset stays
// visibly custom.
function periodMatchesPreset(value, key) {
  return value?.presetKey === key;
}

export default function PeriodPicker({ value, onChange }) {
  const [open, setOpen] = useState(false);

  // P0 (Regel 51): the SWISS business month, from ONE zone conversion — the same
  // source the page's default period and `presetToPeriod` use. Reading
  // `new Date().getFullYear()` / `.getMonth()` here named the DEVICE's month, so
  // around a month boundary the Von/Bis defaults and the year list could disagree
  // with the period the page had actually selected.
  const { year: cy, month: cm } = currentBusinessPeriod();
  const years = [cy - 2, cy - 1, cy];

  // Default the Von/Bis selects from the current value (from→Von, to→Bis).
  const dFrom = value?.from ?? value?.to ?? { year: cy, month: cm };
  const dTo = value?.to ?? { year: cy, month: cm };
  const [vy, setVy] = useState(dFrom.year);
  const [vm, setVm] = useState(dFrom.month);
  const [by, setBy] = useState(dTo.year);
  const [bm, setBm] = useState(dTo.month);

  // Re-sync Von/Bis when `value` changes (preset click / external). `value` only
  // changes on Anwenden or a preset — both close the popover — so this never
  // clobbers an in-progress edit, and it sets local state (no loop).
  useEffect(() => {
    if (value?.from) {
      setVy(value.from.year);
      setVm(value.from.month);
    }
    if (value?.to) {
      setBy(value.to.year);
      setBm(value.to.month);
    }
  }, [value]);

  const choosePreset = (key) => {
    onChange(presetToPeriod(key));
    setOpen(false);
  };
  const applyCustom = () => {
    onChange({
      mode: "custom",
      from: { year: vy, month: vm },
      to: { year: by, month: bm },
      // No preset identity, whatever the dates work out to. Picking Januar–September
      // by hand in the month of September is not "Dieses Jahr": it is a range that
      // stays those months while YTD would move on, and Performance compares the two
      // differently because of it.
      presetKey: null,
    });
    setOpen(false);
  };
  const invalidRange = vy * 12 + vm > by * 12 + bm; // Von > Bis

  const monthSelectCls =
    "rounded-[6px] border border-gray-200 bg-white px-2 py-1 text-[12px] text-gray-900 outline-none dark:border-[#1c2035] dark:bg-[#141720] dark:text-[#edf0f7]";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="font-mono text-[12px]"
        >
          <Calendar aria-hidden />
          {periodTriggerLabel(value)}
          <ChevronDown aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-auto p-0">
        <div className="flex">
          {/* Presets */}
          <div className="flex min-w-[150px] flex-col gap-[2px] border-r border-gray-200 p-2 dark:border-[#1c2035]">
            {PRESETS.map((p) => {
              const active = periodMatchesPreset(value, p.key);
              return (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => choosePreset(p.key)}
                  // The selection is carried programmatically and not by colour
                  // and weight alone, so a screen reader announces which preset
                  // is in force.
                  aria-pressed={active}
                  className={`rounded-[6px] px-3 py-[6px] text-left text-[12px] transition-colors ${
                    active
                      ? "bg-slate-100 font-medium text-gray-900 dark:bg-[#1c2035] dark:text-[#edf0f7]"
                      : "text-gray-600 hover:bg-slate-50 dark:text-[#8892b0] dark:hover:bg-[#141720]"
                  }`}
                >
                  {p.label}
                </button>
              );
            })}
          </div>

          {/* Free Von–Bis */}
          <div className="flex flex-col gap-[10px] p-3">
            <div>
              <p className="mb-[4px] text-[10px] font-medium uppercase tracking-[0.5px] text-gray-500 dark:text-[#4a5070]">
                Von
              </p>
              <div className="flex gap-[6px]">
                <select
                  aria-label="Von Monat"
                  value={vm}
                  onChange={(e) => setVm(Number(e.target.value))}
                  className={monthSelectCls}
                >
                  {MONTHS_DE.map((nm, i) => (
                    <option key={i} value={i + 1}>
                      {nm}
                    </option>
                  ))}
                </select>
                <select
                  aria-label="Von Jahr"
                  value={vy}
                  onChange={(e) => setVy(Number(e.target.value))}
                  className={monthSelectCls}
                >
                  {years.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <p className="mb-[4px] text-[10px] font-medium uppercase tracking-[0.5px] text-gray-500 dark:text-[#4a5070]">
                Bis
              </p>
              <div className="flex gap-[6px]">
                <select
                  aria-label="Bis Monat"
                  value={bm}
                  onChange={(e) => setBm(Number(e.target.value))}
                  className={monthSelectCls}
                >
                  {MONTHS_DE.map((nm, i) => (
                    <option key={i} value={i + 1}>
                      {nm}
                    </option>
                  ))}
                </select>
                <select
                  aria-label="Bis Jahr"
                  value={by}
                  onChange={(e) => setBy(Number(e.target.value))}
                  className={monthSelectCls}
                >
                  {years.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {invalidRange ? (
              <p className="text-[10px] text-rose-600 dark:text-[#ff5f6d]">
                „Von“ darf nicht nach „Bis“ liegen.
              </p>
            ) : null}
            <Button
              type="button"
              size="sm"
              onClick={applyCustom}
              disabled={invalidRange}
              className="self-end"
            >
              Anwenden
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
